import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faChartSimple,
    faClockRotateLeft,
    faMagnifyingGlass
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useClientSearch } from "../new/clientsearch.query";
import { useHomeDashboardQuery } from "../new/home-dashboard.query";
import {
    maxRecentClients,
    normalizeRecentClients
} from "../utilities/recent-clients";
import styles from "../styles/home-dashboard.module.css";
import useUserStore from "../stores/user-store";
import { getApplicationLandingPath, hasAnyPermission, hasPermission, permissionPaths } from "../auth/permission-paths";

const initialSearchValues = {
    applicationid: "",
    medicaidid: "",
    firstname: "",
    lastname: ""
};

const normalizeClientSearchResults = (data) => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.results)) {
        return data.results;
    }

    return [];
};

const getTrimmedSearchValues = (values) => {
    return Object.entries(values).reduce((acc, [key, value]) => {
        acc[key] = typeof value === "string" ? value.trim() : value;
        return acc;
    }, {});
};

const getSearchPayload = (values) => {
    return Object.entries(values).reduce((acc, [key, value]) => {
        if (value) {
            acc[key] = value;
        }

        return acc;
    }, {});
};

const isValidSearch = (values) => {
    return Boolean(values.applicationid || values.medicaidid || (values.firstname && values.lastname));
};

const getResultName = (record) => {
    const firstName = record.firstname ?? record.fName ?? "";
    const lastName = record.lastname ?? record.lName ?? "";
    const name = record.name ?? [firstName, lastName].filter(Boolean).join(" ");

    return name || "Client";
};

const looksLikeProtectedValue = (value) => {
    if (typeof value !== "string") {
        return false;
    }

    const trimmed = value.trim();

    return /^[a-f0-9]{64}$/i.test(trimmed) || trimmed.length > 40;
};

const getRecentClientName = (client) => {
    return looksLikeProtectedValue(client?.name) ? "Client" : client?.name || "Client";
};

const getRecentClientDetail = (client) => {
    if (client?.applicationid) {
        return `Application ${client.applicationid}`;
    }

    return looksLikeProtectedValue(client?.medicaidid) || !client?.medicaidid
        ? "Client record"
        : `Medicaid ${client.medicaidid}`;
};

const getStatusBadgeVariant = (statusGroup, statusText) => {
    const status = statusGroup || statusText;

    switch (status) {
        case "NOT STARTED":
            return "secondary";
        case "Transitioned":
        case "TRANSITIONED":
            return "success";
        case "Denial":
        case "DENY":
            return "danger";
        default:
            return "warning";
    }
};

const HomeDashboard = () => {
    const navigate = useNavigate();
    const clientSearch = useClientSearch();
    const permissions = useUserStore((state) => state.permissions);
    const [searchValues, setSearchValues] = useState(initialSearchValues);
    const [searchResults, setSearchResults] = useState([]);
    const [searchFeedback, setSearchFeedback] = useState(null);
    const [transitionMonths, setTransitionMonths] = useState(12);
    const [transitionGroupBy, setTransitionGroupBy] = useState("month");
    const canViewRecentClients = hasAnyPermission(permissions, [
        permissionPaths.clientApplications.display,
        permissionPaths.clientIncidents.display
    ]);
    const canUseQuickClientSearch = hasPermission(permissions, permissionPaths.clientSearch.display);
    const visiblePanelCount = [canViewRecentClients, canUseQuickClientSearch, true].filter(Boolean).length;
    const panelLgColumns = visiblePanelCount === 1 ? 12 : visiblePanelCount === 2 ? 6 : 4;

    const dashboardPayload = useMemo(() => ({
        transitionMonths,
        transitionGroupBy
    }), [transitionGroupBy, transitionMonths]);

    const dashboardQuery = useHomeDashboardQuery(dashboardPayload);
    const refetchDashboard = dashboardQuery.refetch;

    useEffect(() => {
        if (!canViewRecentClients) {
            return;
        }

        const handleRecentClientsUpdated = () => {
            refetchDashboard();
        };

        window.addEventListener("mfp-recent-clients-updated", handleRecentClientsUpdated);

        return () => {
            window.removeEventListener("mfp-recent-clients-updated", handleRecentClientsUpdated);
        };
    }, [canViewRecentClients, refetchDashboard]);

    const displayedRecentClients = useMemo(
        () => normalizeRecentClients(canViewRecentClients ? dashboardQuery.data?.recentClients ?? [] : []),
        [canViewRecentClients, dashboardQuery.data?.recentClients]
    );
    const transitionsByTime = dashboardQuery.data?.transitionsByTime;
    const transitionBuckets = transitionsByTime?.buckets ?? [];
    const maxTransitionCount = Math.max(...transitionBuckets.map((bucket) => bucket.count), 0);
    const trimmedSearchValues = useMemo(() => getTrimmedSearchValues(searchValues), [searchValues]);
    const canSearch = isValidSearch(trimmedSearchValues);
    const isSearching = clientSearch.isPending;

    const getClientTargetUrl = useCallback((client) => {
        if(client?.applicationid){
            return getApplicationLandingPath(permissions, client.applicationid);
        }

        return client?.targetUrl || null;
    },[permissions]);

    const updateSearchValue = (event) => {
        const { name, value } = event.target;
        setSearchValues((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearchSubmit = async (event) => {
        event.preventDefault();

        if(!canUseQuickClientSearch){
            return;
        }

        if (!canSearch) {
            setSearchFeedback({
                variant: "warning",
                body: "Use Application ID, Medicaid ID, or first and last name."
            });
            return;
        }

        try {
            const data = await clientSearch.mutateAsync(getSearchPayload(trimmedSearchValues));
            const nextResults = normalizeClientSearchResults(data);
            setSearchResults(nextResults.slice(0, 3));
            setSearchFeedback({
                variant: nextResults.length > 0 ? "success" : "warning",
                body: nextResults.length === 1
                    ? "Found 1 client."
                    : nextResults.length > 1
                        ? `Found ${nextResults.length} clients.`
                        : "No clients found."
            });
        } catch (error) {
            setSearchResults([]);
            setSearchFeedback({
                variant: "danger",
                body: error?.message ?? "Unable to search for clients."
            });
        }
    };

    const clearSearch = () => {
        setSearchValues(initialSearchValues);
        setSearchResults([]);
        setSearchFeedback(null);
        clientSearch.reset();
    };

    const openSearchResult = (record) => {
        const targetUrl = record.applicationid
            ? getApplicationLandingPath(permissions, record.applicationid)
            : `/Client/Client/${record.medicaididenc}`;

        if(!targetUrl){
            setSearchFeedback({
                variant: "warning",
                body: "You do not have access to any pages for that application."
            });
            return;
        }

        navigate(targetUrl);
    };

    return (
        <Row className="g-3 mb-3">
            {canViewRecentClients ? (
                <Col xs={12} lg={panelLgColumns}>
                    <section className={`clientCard h-100 ${styles.panel}`} aria-labelledby="home-recent-title">
                        <div className="clientCardInner">
                            <div className={styles.panelHeader}>
                                <div>
                                    <h4 id="home-recent-title" className={styles.panelTitle}>Recent Clients</h4>
                                </div>
                                {dashboardQuery.isFetching ? <Spinner animation="border" size="sm" /> : <Badge bg="secondary">{displayedRecentClients.length} / {maxRecentClients}</Badge>}
                            </div>

                            {displayedRecentClients.length > 0 ? (
                                <div className={styles.recentScrollArea}>
                                    <div className={styles.recentList}>
                                        {displayedRecentClients.map((client) => {
                                            const targetUrl = getClientTargetUrl(client);

                                            return (
                                                <button
                                                    type="button"
                                                    key={client.key}
                                                    className={styles.recentItem}
                                                    disabled={!targetUrl}
                                                    onClick={() => targetUrl && navigate(targetUrl)}
                                                >
                                                    <span className={styles.recentIcon} aria-hidden="true">
                                                        <FontAwesomeIcon icon={faClockRotateLeft} />
                                                    </span>
                                                    <span className={styles.recentBody}>
                                                        <strong>{getRecentClientName(client)}</strong>
                                                        <span>{getRecentClientDetail(client)}</span>
                                                    </span>
                                                    <Badge bg={getStatusBadgeVariant(client.statusgroup, client.applicationstatus)}>
                                                        {(client.applicationstatus || "NOT STARTED").toUpperCase()}
                                                    </Badge>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.emptyState}>Open a client record to start building this list.</div>
                            )}
                        </div>
                    </section>
                </Col>
            ) : null}

            {canUseQuickClientSearch ? (
                <Col xs={12} lg={panelLgColumns}>
                    <section className={`clientCard h-100 ${styles.panel}`} aria-labelledby="home-quick-search-title">
                        <div className="clientCardInner">
                            <div className={styles.panelHeader}>
                                <div>
                                    <h4 id="home-quick-search-title" className={styles.panelTitle}>Quick Client Search</h4>
                                </div>
                                <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.headerIcon} aria-hidden="true" />
                            </div>

                            <Form onSubmit={handleSearchSubmit} className={styles.quickSearchForm}>
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <Form.Control
                                            name="applicationid"
                                            type="number"
                                            inputMode="numeric"
                                            placeholder="Application ID"
                                            value={searchValues.applicationid}
                                            onChange={updateSearchValue}
                                            disabled={isSearching}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Control
                                            name="medicaidid"
                                            type="text"
                                            placeholder="Medicaid ID"
                                            value={searchValues.medicaidid}
                                            onChange={updateSearchValue}
                                            disabled={isSearching}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Control
                                            name="firstname"
                                            type="text"
                                            placeholder="First Name"
                                            value={searchValues.firstname}
                                            onChange={updateSearchValue}
                                            disabled={isSearching}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Control
                                            name="lastname"
                                            type="text"
                                            placeholder="Last Name"
                                            value={searchValues.lastname}
                                            onChange={updateSearchValue}
                                            disabled={isSearching}
                                        />
                                    </Col>
                                </Row>
                                <div className={styles.quickSearchActions}>
                                    <Button type="submit" variant="success" size="sm" disabled={isSearching || !canSearch}>
                                        {isSearching ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                                        Search
                                    </Button>
                                    <Button type="button" variant="outline-secondary" size="sm" onClick={clearSearch} disabled={isSearching}>
                                        Clear
                                    </Button>
                                </div>
                            </Form>

                            {searchFeedback ? (
                                <Alert variant={searchFeedback.variant} className="py-2 px-3 mt-3 mb-0">
                                    {searchFeedback.body}
                                </Alert>
                            ) : null}

                            {searchResults.length > 0 ? (
                                <div className={styles.searchResultsList}>
                                    {searchResults.map((record) => (
                                        <button
                                            type="button"
                                            key={`${record.applicationid ?? "client"}-${record.medicaidid}`}
                                            className={styles.searchResultItem}
                                            onClick={() => openSearchResult(record)}
                                        >
                                            <span>
                                                <strong>{getResultName(record)}</strong>
                                                <small>{record.applicationid ? `Application ${record.applicationid}` : `Medicaid ${record.medicaidid}`}</small>
                                            </span>
                                            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </section>
                </Col>
            ) : null}

            <Col xs={12} lg={panelLgColumns}>
                <section className={`clientCard h-100 ${styles.panel}`} aria-labelledby="home-transition-title">
                    <div className="clientCardInner">
                        <div className={styles.panelHeader}>
                            <div>
                                <h4 id="home-transition-title" className={styles.panelTitle}>Transitions by Time</h4>
                            </div>
                            <FontAwesomeIcon icon={faChartSimple} className={styles.headerIcon} aria-hidden="true" />
                        </div>

                        <div className={styles.reportControls}>
                            <Form.Select
                                size="sm"
                                aria-label="Transition report range"
                                value={transitionMonths}
                                onChange={(event) => setTransitionMonths(Number(event.target.value))}
                            >
                                <option value={6}>Last 6 months</option>
                                <option value={12}>Last 12 months</option>
                                <option value={24}>Last 24 months</option>
                                <option value={36}>Last 36 months</option>
                            </Form.Select>
                            <Form.Select
                                size="sm"
                                aria-label="Transition report grouping"
                                value={transitionGroupBy}
                                onChange={(event) => setTransitionGroupBy(event.target.value)}
                            >
                                <option value="month">Monthly</option>
                                <option value="quarter">Quarterly</option>
                                <option value="year">Yearly</option>
                            </Form.Select>
                        </div>

                        {dashboardQuery.isLoading ? (
                            <div className={styles.emptyState}>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading transitions
                            </div>
                        ) : dashboardQuery.isError ? (
                            <Alert variant="danger" className="py-2 px-3 mt-3 mb-0">
                                Transitions could not be loaded.
                            </Alert>
                        ) : (
                            <div className={styles.chartPanel}>
                                <div className={styles.chartSummary}>
                                    <span>Total</span>
                                    <strong>{transitionsByTime?.total ?? 0}</strong>
                                </div>
                                <div className={styles.chartRows}>
                                    {transitionBuckets.map((bucket) => {
                                        const width = maxTransitionCount > 0 ? `${Math.max((bucket.count / maxTransitionCount) * 100, 4)}%` : "0%";

                                        return (
                                            <div className={styles.chartRow} key={`${bucket.label}-${bucket.startDate}`}>
                                                <span className={styles.chartLabel}>{bucket.label}</span>
                                                <span className={styles.chartTrack}>
                                                    <span className={styles.chartBar} style={{ width }} />
                                                </span>
                                                <strong>{bucket.count}</strong>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </Col>
        </Row>
    );
};

export default HomeDashboard;
